import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowRightLeft, Plus } from 'lucide-react';
import { useState } from 'react';

import {
  CatalogCard,
  CatalogGrid,
  CatalogGroup,
  CatalogHead,
  CatalogSearch,
  CatalogTable,
  NewCard,
  ViewToggle,
} from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { useViewMode } from '@/features/catalog/useViewMode';
import { Sketch } from '@/features/catalog/Sketch';
import { sketchOf } from '@/features/catalog/sketchOf';
import { BUILTIN_PREFIX, useBlockTypes } from '@/features/catalog/useBlockTypes';
import { usePendingTransfer, useSchemaTypes } from '@/features/catalog/useSchemaTypes';
import type { BlockTypeBody, TypeBody } from '@/shared/api/generated/model';
import { pluralize } from '@/shared/lib/plural';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';

const FIELDS = ['поле', 'поля', 'полей'] as const;
const NO_GROUP = 'Без группы';

/**
 * «Компоненты» — секции, из которых собираются страницы: плитками с эскизом,
 * по группам («Первые экраны», «Подборки»…). Встроенные — отдельной группой
 * в конце. Клик — деталка компонента.
 */
export function ComponentsPage() {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env/components/' });
  const navigate = useNavigate();
  const [view, setView] = useViewMode('components');
  const [search, setSearch] = useState('');
  const pending = usePendingTransfer(siteId, env);

  const types = useSchemaTypes(siteId, env);
  const blocks = useBlockTypes(siteId, env);

  const needle = search.trim().toLowerCase();
  const matches = (text: string) => needle === '' || text.toLowerCase().includes(needle);

  const fieldsOf = (slug: string) => blocks.data?.find((item) => item.type === slug)?.fields ?? [];
  const own = (types.data ?? [])
    .filter((type) => type.kind === 'block')
    .filter((type) => matches(`${type.name} ${type.slug}`));
  const builtin = (blocks.data ?? [])
    .filter((item) => item.type_id === undefined)
    .filter((item) => matches(`${item.title} ${item.type}`));

  const groups = new Map<string, TypeBody[]>();

  for (const type of own) {
    const name = type.folder === '' ? NO_GROUP : type.folder;
    groups.set(name, [...(groups.get(name) ?? []), type]);
  }

  // «Без группы» — после названных групп.
  const ordered = [...groups].sort(([a], [b]) => Number(a === NO_GROUP) - Number(b === NO_GROUP));

  const create = () => {
    void navigate({ to: '/sites/$siteId/$env/components/new', params: { siteId, env } });
  };

  const ownCard = (type: TypeBody) => (
    <CatalogCard
      key={type.id}
      link={{
        to: '/sites/$siteId/$env/components/$typeId',
        params: { siteId, env, typeId: type.id },
      }}
      preview={<Sketch kind={sketchOf(type.slug, fieldsOf(type.slug))} />}
      title={type.name}
      subtitle={type.slug}
      meta={<span>{pluralize(fieldsOf(type.slug).length, FIELDS)}</span>}
    />
  );

  const builtinCard = (item: BlockTypeBody) => (
    <CatalogCard
      key={item.type}
      link={{
        to: '/sites/$siteId/$env/components/$typeId',
        params: { siteId, env, typeId: `${BUILTIN_PREFIX}${item.type}` },
      }}
      preview={<Sketch kind={sketchOf(item.type, [])} />}
      title={item.title}
      subtitle={item.type}
      meta={<span>встроенный · {pluralize((item.fields ?? []).length, FIELDS)}</span>}
    />
  );

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title="Компоненты"
        subtitle="Секции, из которых собираются страницы: первые экраны, подборки, тексты. Группа задаётся в настройках компонента."
        actions={
          <>
            <ViewToggle value={view} onChange={setView} />
            <Button
              asChild
              variant="outline"
              tone="neutral"
              iconLeft={<ArrowRightLeft size={15} />}
            >
              <Link to="/sites/$siteId/$env/transfer" params={{ siteId, env }}>
                Перенос
                {pending > 0 && (
                  <Badge size="sm" tone="accent">
                    {pending}
                  </Badge>
                )}
              </Link>
            </Button>
            <Button iconLeft={<Plus size={15} />} onClick={create}>
              Новый компонент
            </Button>
          </>
        }
      />

      <div className={catalog['bar']}>
        <CatalogSearch value={search} onChange={setSearch} placeholder="Поиск компонента" />
      </div>

      {view === 'grid' ? (
        <>
          {ordered.map(([group, list]) => (
            <CatalogGroup key={group} title={group} count={list.length}>
              <CatalogGrid>{list.map(ownCard)}</CatalogGrid>
            </CatalogGroup>
          ))}

          {own.length === 0 && needle === '' && (
            <CatalogGrid>
              <NewCard onClick={create}>
                <Plus size={20} />
                Первый компонент — например, «Первый экран»
              </NewCard>
            </CatalogGrid>
          )}

          {builtin.length > 0 && (
            <CatalogGroup title="Встроенные" count={builtin.length}>
              <CatalogGrid>{builtin.map(builtinCard)}</CatalogGrid>
            </CatalogGroup>
          )}
        </>
      ) : (
        <CatalogTable head={['Название', 'Ключ', 'Группа', 'Полей']}>
          {own.map((type) => (
            <tr key={type.id}>
              <td>
                <Link
                  to="/sites/$siteId/$env/components/$typeId"
                  params={{ siteId, env, typeId: type.id }}
                >
                  {type.name}
                </Link>
              </td>
              <td className={catalog['mono']}>{type.slug}</td>
              <td>{type.folder || '—'}</td>
              <td className={catalog['num']}>{fieldsOf(type.slug).length}</td>
            </tr>
          ))}
          {builtin.map((item) => (
            <tr key={item.type}>
              <td>
                <Link
                  to="/sites/$siteId/$env/components/$typeId"
                  params={{ siteId, env, typeId: `${BUILTIN_PREFIX}${item.type}` }}
                >
                  {item.title}
                </Link>
              </td>
              <td className={catalog['mono']}>{item.type}</td>
              <td>Встроенные</td>
              <td className={catalog['num']}>{(item.fields ?? []).length}</td>
            </tr>
          ))}
        </CatalogTable>
      )}
    </div>
  );
}
